import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../pages/Dashboard/components/DashboardWrapper'
import InnerBanner from '../../components/InnerBanner'
import Button from '../../components/Button'
import Loader from '../../components/Loader'
import Toast from '../../components/Toast/Toast'
import { PRIVATE_ROUTES } from '../../Routes/routes'
import { updateUserFastCompany } from '../../services/Admin'
import {
  getContractDocuments,
  newRegisterCompany,
} from '../../services/NewServicesCompnay'
import { getCompanyInformation } from '../../services/CompanyData'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { TAGS } from '../../services/Tags'
import {
  extractCompanyTokenFromResponse,
  saveCompanyCredentials,
} from '../../utils/companyLogin'
import ContractDocumentPicker from '../../components/ContractDocumentPicker/ContractDocumentPicker'
import {
  CONTRACT_PROFILE_ID,
  extractCompanyContractIds,
  filterContractDocumentsByProfile,
} from '../../utils/contractDocuments'

const generateCompanyCredentials = (companyName) => {
  const base = companyName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 12)
  const suffix = Math.floor(1000 + Math.random() * 9000)
  const username = `${base}${suffix}`
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  const password = Array.from({ length: 12 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('')
  return { username, password }
}

const buildNewCompanyPayload = (formData, contractIds) => {
  const { username, password } = generateCompanyCredentials(formData.companyName)
  const normalizedContractIds = contractIds.map(Number).filter(Boolean)

  const isSingleContract = normalizedContractIds.length === 1

  return {
    companyName: formData.companyName.trim(),
    contactName: formData.contactName.trim(),
    contactEmail: formData.contactEmail.trim(),
    contactPhone: (formData.contactPhone || '').trim(),
    legalContactName: (formData.legalContactName || '').trim(),
    legalContactDni: (formData.legalContactDni || '').trim(),
    legalContactPhone: (formData.legalContactPhone || '').trim(),
    email: formData.email.trim(),
    username,
    password,
    ...(isSingleContract
      ? { contractId: normalizedContractIds[0] }
      : { contractIds: normalizedContractIds }),
  }
}

const CreateNewFastCompany = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isEditMode = !!id
  const redirectTo = location.state?.redirectTo || PRIVATE_ROUTES.admin_companies

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  const { data: companyData, isLoading: isLoadingCompany } = useQuery({
    queryKey: [TAGS.company_detail, id],
    queryFn: () => getCompanyInformation(id),
    enabled: isEditMode,
  })

  const { data: contractDocuments = [], isLoading: isLoadingContractDocuments } = useQuery(
    [TAGS.contract_documents],
    getContractDocuments,
    {
      select: (data) => {
        const list = data?.res?.documents
        const documents = Array.isArray(list) ? list : []
        return filterContractDocumentsByProfile(
          documents,
          CONTRACT_PROFILE_ID.COMPANY
        )
      },
    }
  )

  useEffect(() => {
    if (isEditMode && companyData) {
      reset({
        companyName:  companyData.name || '',
        contactName:  companyData.contact_name || '',
        contactEmail: companyData.email || '',
        email:        companyData.billing_email || companyData.email || '',
        contactPhone: companyData.phone || '',
        legalContactName:  companyData.legal_contact_name || '',
        legalContactDni:   companyData.legal_contact_dni || '',
        legalContactPhone: companyData.legal_contact_phone || '',
        companyDescription: companyData.review || '',
      })
    }
  }, [companyData, isEditMode, reset])

  const pendingCredentials = useRef(null)

  const { mutate: createCompanyMutate, isLoading: isCreating } = useMutation(
    newRegisterCompany,
    {
      onSuccess: ({ res }) => {
        if (pendingCredentials.current) {
          const companyToken = extractCompanyTokenFromResponse(res)
          saveCompanyCredentials(pendingCredentials.current.companyName, {
            ...pendingCredentials.current,
            companyToken,
          })
        }
        pendingCredentials.current = null
        queryClient.invalidateQueries([TAGS.list_of_companies])
        queryClient.invalidateQueries([TAGS.list_of_accountants_companies])
        Toast({ title: 'Empresa creada con éxito', timer: 2000 }).finally(() => {
          navigate(redirectTo)
        })
      },
      onError: (error) => {
        pendingCredentials.current = null
        Toast({
          title: error?.message || 'No se pudo crear la empresa',
          icon: 'error',
          timer: 2500,
        })
      },
    }
  )

  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedContractIds, setSelectedContractIds] = useState([])
  const [contractSelectionTouched, setContractSelectionTouched] = useState(false)

  const contractValidationMessage =
    !isEditMode && contractSelectionTouched && selectedContractIds.length === 0
      ? 'Selecciona al menos un tipo de contrato'
      : ''

  const fillTestData = () => {
    const id = Date.now()
    reset({
      companyName: `Empresa Prueba ${id}`,
      contactName: 'Contacto de Prueba',
      contactEmail: `prueba+${id}@test.hutrit.com`,
      email: `empresa+${id}@test.hutrit.com`,
      contactPhone: '45612312312',
      legalContactName: 'Representante Legal de Prueba',
      legalContactDni: '12345678A',
      legalContactPhone: '600123456',
      companyDescription: 'Descripción de prueba generada automáticamente.',
    })
    const firstActive =
      contractDocuments.find((doc) => doc.active) ?? contractDocuments[0]
    setSelectedContractIds(firstActive?.id ? [String(firstActive.id)] : [])
    setContractSelectionTouched(false)
  }

  useEffect(() => {
    if (!isEditMode || !companyData) return
    const ids = extractCompanyContractIds(companyData)
    setSelectedContractIds(ids.map(String))
  }, [companyData, isEditMode])

  const onSubmit = async (data) => {
    if (!isEditMode && selectedContractIds.length === 0) {
      setContractSelectionTouched(true)
      return
    }
    setContractSelectionTouched(false)
    if (isEditMode) {
      setIsUpdating(true)
      try {
        await updateUserFastCompany({
          ...data,
          phone: data.contactPhone,
          billingEmail: data.email,
          comanyId: id,
        })
        queryClient.invalidateQueries([TAGS.company_detail, id])
        queryClient.invalidateQueries([TAGS.list_of_companies])
        queryClient.invalidateQueries([TAGS.list_of_accountants_companies])
        Toast({ title: 'Información actualizada con éxito', timer: 2000 }).finally(() => {
          navigate(PRIVATE_ROUTES.admin_companies)
        })
      } catch {
        Toast({ title: 'Error al actualizar la información', icon: 'error', timer: 2000 })
      } finally {
        setIsUpdating(false)
      }
    } else {
      const payload = buildNewCompanyPayload(data, selectedContractIds)
      pendingCredentials.current = {
        username: payload.username,
        password: payload.password,
        companyName: payload.companyName,
      }
      createCompanyMutate(payload)
    }
  }

  const fieldClass = 'form-control form-control-sm'
  const labelClass = 'form-label text-on-bg-primary mb-1'
  const fieldWrapClass = 'col-12 col-md-6'

  return (
    <DashboardWrapper>
      <div className="p-2 p-lg-3">
        <div className="mx-auto w-100" style={{ maxWidth: '1040px' }}>
          <InnerBanner
            className="mb-2"
            before="Empresas"
            beforeRoute={PRIVATE_ROUTES.admin_companies}
            text={isEditMode ? 'Editar empresa' : 'Nueva empresa'}
          />

          {isEditMode && isLoadingCompany ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '280px' }}
            >
              <Loader />
            </div>
          ) : (
            <form
              className="border rounded bg-white p-3 p-md-4"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-3"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                <div>
                  <h2
                    className="text-bg-brand mb-0"
                    style={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.25 }}
                  >
                    {isEditMode ? 'Edición de empresa' : 'Creación rápida de empresa'}
                  </h2>
                  <p
                    className="text-on-bg-secondary mb-0 mt-1"
                    style={{ fontSize: '13px' }}
                  >
                    Datos generales
                  </p>
                </div>
                {!isEditMode && process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={fillTestData}
                    className="flex-shrink-0"
                    style={{
                      background: 'none',
                      border: '1px dashed #d1d5db',
                      borderRadius: '6px',
                      padding: '4px 12px',
                      fontSize: '11px',
                      color: '#9ca3af',
                      cursor: 'pointer',
                    }}
                  >
                    Rellenar datos de prueba
                  </button>
                )}
              </div>

              <div className="row g-2">
                <div className="col-12">
                  <label className={labelClass}>Nombre de la empresa *</label>
                  <input
                    className={fieldClass}
                    type="text"
                    {...register('companyName', { required: 'Este campo es obligatorio' })}
                  />
                  {errors.companyName && (
                    <span className="text-danger font-small">{errors.companyName.message}</span>
                  )}
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Nombre del contacto *</label>
                  <input
                    className={fieldClass}
                    type="text"
                    {...register('contactName', { required: 'Este campo es obligatorio' })}
                  />
                  {errors.contactName && (
                    <span className="text-danger font-small">{errors.contactName.message}</span>
                  )}
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Correo de contacto *</label>
                  <input
                    className={fieldClass}
                    type="email"
                    {...register('contactEmail', { required: 'Este campo es obligatorio' })}
                  />
                  {errors.contactEmail && (
                    <span className="text-danger font-small">{errors.contactEmail.message}</span>
                  )}
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Correo de la empresa *</label>
                  <input
                    className={fieldClass}
                    type="email"
                    {...register('email', { required: 'Este campo es obligatorio' })}
                  />
                  {errors.email && (
                    <span className="text-danger font-small">{errors.email.message}</span>
                  )}
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Número de teléfono</label>
                  <input
                    className={fieldClass}
                    type="tel"
                    {...register('contactPhone')}
                  />
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Nombre del representante legal</label>
                  <input
                    className={fieldClass}
                    type="text"
                    {...register('legalContactName')}
                  />
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>DNI del representante legal</label>
                  <input
                    className={fieldClass}
                    type="text"
                    {...register('legalContactDni')}
                  />
                </div>

                <div className={fieldWrapClass}>
                  <label className={labelClass}>Teléfono del representante legal</label>
                  <input
                    className={fieldClass}
                    type="tel"
                    {...register('legalContactPhone')}
                  />
                </div>

                <div className="col-12">
                  <label className={labelClass}>Descripción de la empresa</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    style={{ minHeight: '140px', resize: 'vertical' }}
                    {...register('companyDescription')}
                  />
                </div>
              </div>

              <div className="mt-2">
                <ContractDocumentPicker
                  compact
                  label="Tipo de contrato *"
                  documents={contractDocuments}
                  selectedIds={selectedContractIds}
                  onChange={(ids) => {
                    setSelectedContractIds(ids)
                    if (ids.length > 0) setContractSelectionTouched(false)
                  }}
                  multiple
                  isLoading={isLoadingContractDocuments}
                  error={contractValidationMessage || null}
                  hint={
                    selectedContractIds.length > 0
                      ? `${selectedContractIds.length} contrato(s) seleccionado(s)`
                      : null
                  }
                />
              </div>

              <div className="d-flex justify-content-end mt-2">
                <Button
                  disabled={isCreating || isUpdating}
                  className="btn-bg-CTA-primary btn-sm px-4"
                  type="submit"
                >
                  {isCreating || isUpdating ? (
                    <Loader />
                  ) : isEditMode ? (
                    'Actualizar Empresa'
                  ) : (
                    'Crear Empresa'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardWrapper>
  )
}

export default CreateNewFastCompany
